/**
 * `<VmDebugPane>` — root component for the B-1 maintainer-mode VM
 * Debug pane (cloud-VM supportability TDD §B-1).
 *
 * Replaces the `<FilesPanel>` in `Workspace.tsx` when the parent
 * gates this component on `maintainerMode === true`. The pane is the
 * single-screen vehicle for the five sections enumerated by the
 * TDD:
 *
 *   1. `<ConnectionStateStrip>` — sticky top
 *   2. `<MessageStream>` — main scrollable body (virtualised)
 *   3. `<FilterChips>` — sticky below the connection strip
 *   4. `<ActiveSpans>` — sticky above CapCohort
 *   5. `<CapCohortStatePanel>` — bottom, collapsed by default
 *
 * # Why the maintainer-mode gate lives at the call site
 *
 * Per A-1's lesson learned (`<VmStatusPill>`'s first iteration
 * pulled `useFeatureFlags` into the component and broke 13 SSR
 * tests), this component does NOT call `useMaintainerMode()` itself.
 * The parent `Workspace.tsx` reads the hook once and threads the
 * boolean through the conditional mount. The pane unmounts entirely
 * when the engineer flag is OFF — there is no internal flag check
 * here.
 *
 * # State ownership
 *
 * The pane owns:
 *
 *   - `enabledKinds` — the filter chip set (default: all enabled).
 *   - `expanded` — per-seq expansion map (drives row-height remeasure).
 *   - `reveal` — the `useRawReveal()` state machine.
 *
 * Each section consumes a subset; the pane is the single coordinator.
 *
 * # Flag-OFF safety (mainline)
 *
 * Even if a future refactor accidentally mounts this component with
 * `maintainerMode === false`, the children's IPC subscriptions are
 * silent under flag-OFF (no producer in `main/index.ts`) and the
 * empty-state surfaces render. There is no privileged data leak
 * here — the gate is the mount gate.
 */

import { useCallback, useState } from 'react';
import type { VmMessageKind } from '@volley/crucible-ipc-types/vm-message-stream';
import { ConnectionStateStrip } from './ConnectionStateStrip';
import { FilterChips, ALL_FILTER_KINDS } from './FilterChips';
import { MessageStream } from './MessageStream';
import { ActiveSpans } from './ActiveSpans';
import { CapCohortStatePanel } from './CapCohortStatePanel';
import { RawRevealOverlay } from './RawRevealOverlay';
import { useMessageStream } from './hooks/useMessageStream';
import { useRawReveal } from './hooks/useRawReveal';

export interface VmDebugPaneProps {
  /** Optional fixed height for the virtualised message stream. Mostly
   *  for tests; production lets the flexbox parent dictate. */
  streamHeight?: number;
  /** Optional initial-expanded state for the cap/cohort panel. Tests
   *  pass `true` to assert the rendered content. */
  initialCapCohortExpanded?: boolean;
}

/**
 * The pane. Mounted by `Workspace.tsx` only when `maintainerMode` is
 * ON — see the parent for the gate. No internal flag-check; the
 * presence of the mount IS the gate.
 */
export function VmDebugPane({
  streamHeight,
  initialCapCohortExpanded,
}: VmDebugPaneProps = {}): React.JSX.Element {
  // Default filter set: all kinds enabled. Operators mute heartbeat
  // / lifecycle later if needed.
  const [enabledKinds, setEnabledKinds] = useState<Set<VmMessageKind>>(
    () => new Set(ALL_FILTER_KINDS),
  );
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const reveal = useRawReveal();
  const stream = useMessageStream();

  const onToggleFilter = useCallback((kind: VmMessageKind) => {
    setEnabledKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) {
        next.delete(kind);
      } else {
        next.add(kind);
      }
      return next;
    });
  }, []);

  const onToggleExpanded = useCallback((seq: number) => {
    setExpanded((prev) => ({ ...prev, [seq]: !prev[seq] }));
  }, []);

  return (
    <aside
      data-testid="vm-debug-pane"
      data-message-count={stream.messages.length}
      className="relative flex w-[400px] min-w-[400px] flex-col border-r border-white/10 bg-black/20"
    >
      <RawRevealOverlay reveal={reveal}>
        <ConnectionStateStrip />
        <FilterChips enabledKinds={enabledKinds} onToggle={onToggleFilter} />
        <MessageStream
          messages={stream.messages}
          enabledKinds={enabledKinds}
          expanded={expanded}
          onToggleExpanded={onToggleExpanded}
          rawActive={reveal.rawActive}
          height={streamHeight}
        />
        <ActiveSpans messages={stream.messages} />
        {/* Bugbot round-4 LOW fix: pass `initialCapCohortExpanded` as
            a real `defaultExpanded` prop on `<CapCohortStatePanel>`
            instead of remounting via `key` (which silently reset the
            child's internal state to collapsed, defeating the prop). */}
        <CapCohortStatePanel defaultExpanded={initialCapCohortExpanded ?? false} />
      </RawRevealOverlay>
    </aside>
  );
}
